import { Controller } from '@hotwired/stimulus';

/**
 * Generic value-driven enable/disable controller.
 *
 * Wires a "trigger" input (radio group, checkbox, or select) to "targetGroup" containers.
 * When the trigger value changes, the target group whose `data-value` matches is enabled
 * and the others are disabled. `data-value` may list multiple values separated by commas
 * (e.g. `data-value="a,b"`).
 *
 * By default, disabled groups are greyed out (`text-muted`) and their inputs are disabled.
 * Set `data-value-toggle-hide-value="true"` on the controller element to hide non-matching
 * groups (via `d-none`) instead of greying them out.
 *
 * Usage with radios:
 *   div data-controller="value-toggle"
 *     input type="radio" value="a" data-value-toggle-target="trigger" data-action="change->value-toggle#refresh"
 *     input type="radio" value="b" data-value-toggle-target="trigger" data-action="change->value-toggle#refresh"
 *     div data-value-toggle-target="targetGroup" data-value="a"
 *       input type="text"
 *     div data-value-toggle-target="targetGroup" data-value="b"
 *       input type="text"
 *
 * Usage with a select, hiding inactive groups:
 *   div data-controller="value-toggle" data-value-toggle-hide-value="true"
 *     select data-value-toggle-target="trigger" data-action="change->value-toggle#refresh"
 *       option value="a"
 *       option value="b"
 *     div data-value-toggle-target="targetGroup" data-value="a,b"
 *       input type="text"
 */
export default class extends Controller {
  static targets = ['trigger', 'targetGroup']

  static values = { hide: Boolean }

  connect() {
    this.refresh();
  }

  refresh() {
    const value = this.currentValue();

    this.targetGroupTargets.forEach((group) => {
      const allowed = (group.dataset.value || '').split(',').map((v) => v.trim());
      const enabled = allowed.includes(value);
      if (this.hideValue) {
        group.classList.toggle('d-none', !enabled);
      } else {
        group.classList.toggle('text-muted', !enabled);
      }
      group.querySelectorAll('input, select, textarea').forEach((field) => {
        field.disabled = !enabled;
      });
    });
  }

  currentValue() {
    const trigger = this.triggerTargets.find((target) => {
      if (target.type === 'radio' || target.type === 'checkbox') return target.checked;
      return true;
    });
    return trigger?.value;
  }
}
